import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, ChevronDown } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Btn, Empty, Modal, Page, Spinner, TopBar } from "@/components/kit";
import { clearGuest, getGuest } from "@/lib/guest";
import EventPicker from "@/components/EventPicker";
import { CategoryPicker, ContactFields } from "@/pages/EditContact";
import { useScanDraft } from "@/context/auth";
import { useAddToEvent, useEvents, useSelectedEvent } from "@/hooks/queries";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/toto";

const KEYS = { name: "fullName" };

function useObjectUrl(file) {
  const [u, setU] = useState(null);
  useEffect(() => {
    if (!file) return;
    const x = URL.createObjectURL(file);
    setU(x);
    return () => URL.revokeObjectURL(x);
  }, [file]);
  return u;
}

const claims = new Map();
const claim = (path) => {
  if (!claims.has(path)) claims.set(path, api.post("/scans/claim-guest", { guestImagePath: path }).then((r) => r?.cardImagePath ?? null));
  return claims.get(path);
};

export default function ScanReview() {
  const { draft, setDraft } = useScanDraft();
  const pending = draft?.guest ? draft : !draft ? getGuest() : null;
  useEffect(() => {
    if (!pending) return;
    let live = true;
    window.scrollTo(0, 0);
    claim(pending.cardImagePath)
      .catch(() => { toast.error("Couldn't attach the card image, but you can still save the contact."); return null; })
      .then((path) => live && setDraft({ ...pending, guest: false, claimed: true, cardImagePath: path }));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!pending]);
  if (pending) return <Page><Spinner className="py-24" /></Page>;
  return <Review key={draft?.cardImagePath ?? "none"} />;
}

function Review() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { draft, setDraft } = useScanDraft();
  const events = useEvents({ sort: "date" }).data ?? [];
  const [selectedId, setSelectedId] = useSelectedEvent();
  const addToEvent = useAddToEvent();
  const [form, setForm] = useState(() => draft?.extracted ?? {});
  const [category, setCategory] = useState(draft?.extracted?.category ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dup, setDup] = useState(null);
  const [picker, setPicker] = useState(false);
  const frontUrl = useObjectUrl(draft?.localFront);
  const backUrl = useObjectUrl(draft?.localBack);
  const flagged = useMemo(() => Object.fromEntries(Object.entries(draft?.confidence ?? {}).filter(([, v]) => v === "low" || v === "medium").map(([k]) => [k, true])), [draft]);
  const selected = events.find((e) => e.id === selectedId);

  if (!draft) return <Page><TopBar title="Review Contact" backTo="/scan" /><Empty title="Nothing to review." action={<Link to="/scan" className="t-btn t-btn-primary">Back to Scanner</Link>} /></Page>;

  const payload = () => ({
    name: form.fullName, designation: form.designation, companyName: form.companyName, phone: form.phone, alternatePhone: form.alternatePhone,
    whatsapp: form.whatsapp, email: form.email, website: form.website, address: form.address, area: form.area, city: form.city, state: form.state,
    country: form.country, postalCode: form.postalCode, category, notes: form.notes,
    cardImagePath: draft.cardImagePath, cardBackImagePath: draft.cardBackImagePath, scanId: draft.scanId,
  });
  const link = async (cid) => { if (selectedId) try { await addToEvent.mutateAsync({ eventId: selectedId, contactId: cid }); } catch {} };
  const finish = (cid, msg) => {
    ["contacts", "scanUsage", "categoryCounts", "explore"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    toast.success(msg);
    setDraft(null);
    clearGuest();
    nav(`/contacts/${cid}`, { replace: true });
  };
  const create = async () => {
    setSaving(true);
    try {
      const c = await api.post("/contacts", payload());
      await link(c.id);
      finish(c.id, "Contact saved.");
    } catch (e) {
      setError(e.message || "Could not save contact.");
    } finally {
      setSaving(false);
      setDup(null);
    }
  };
  const updateExisting = async () => {
    setSaving(true);
    try {
      const patch = {};
      Object.entries(payload()).forEach(([k, v]) => {
        if (["scanId", "cardImagePath", "cardBackImagePath"].includes(k)) return;
        if (typeof v === "string" && v.trim()) patch[k] = v;
      });
      await api.patch(`/contacts/${dup.id}`, patch);
      await link(dup.id);
      qc.invalidateQueries({ queryKey: ["contact", dup.id] });
      finish(dup.id, "Existing contact updated.");
    } catch (e) {
      setError(e.message || "Could not update contact.");
    } finally {
      setSaving(false);
      setDup(null);
    }
  };
  const save = async () => {
    if (!form.fullName?.trim() && !form.companyName?.trim()) return setError("Please provide at least a name or company.");
    setError(null);
    try {
      const d = await api.post("/contacts/find-duplicate", { phone: form.phone, email: form.email, name: form.fullName, companyName: form.companyName, website: form.website });
      if (d) return setDup(d);
    } catch {}
    create();
  };

  return (
    <Page>
      <TopBar title="Review Contact" backTo="/scan" testid="review-back" />
      <div className="lg:grid lg:grid-cols-[0.9fr_1.4fr] lg:gap-10">
        <div className="lg:sticky lg:top-16 self-start space-y-3">
          {[frontUrl || draft.thumb, backUrl].filter(Boolean).map((u, i) => <img key={u} src={u} alt="Card" className="w-full rounded-[18px] border border-[#E2E8F0] aspect-[1.7/1] object-cover" data-testid={i ? "review-card-back-image" : "review-card-image"} />)}
          {draft.warning && <p className="text-[13px] text-[#B54708]">{draft.warning}</p>}
          {Object.keys(flagged).length > 0 && <div className="rounded-[14px] bg-[#FFFBEB] border border-[#FDE68A] p-3 text-[14px] font-medium text-[#B54708]" data-testid="review-verify-banner">Please verify the highlighted information.</div>}
        </div>
        <div>
          <h2 className="t-section mt-6 lg:mt-0 mb-3">Category</h2>
          <CategoryPicker value={category} onChange={setCategory} prefix="review" />
          <div className="mt-6"><ContactFields form={form} setForm={setForm} keys={KEYS} flagged={flagged} prefix="review" /></div>
          {error && <p className="mt-4 text-[14px] text-[#DC2626]" data-testid="review-error">{error}</p>}
          <h2 className="t-section mt-8 mb-3">Add to Event</h2>
          <button className="t-input flex items-center gap-3 text-left" onClick={() => setPicker(true)} data-testid="review-event-select">
            <CalendarDays className="w-5 h-5 text-[#0000EC]" />
            <span className="flex-1 truncate">{selected ? `${selected.name} · ${fmtDate(selected.eventDate)}` : "No Event"}</span>
            <ChevronDown className="w-5 h-5 text-[#64748B]" />
          </button>
          <Btn className="w-full mt-8" onClick={save} loading={saving} testid="review-save">Save Contact</Btn>
        </div>
      </div>
      <Modal open={!!dup} onClose={() => setDup(null)} title="Contact May Already Exist" description={`A contact matching “${dup?.name ?? ""}${dup?.companyName ? ` · ${dup.companyName}` : ""}” is already saved.`} testid="duplicate-modal">
        <div className="flex flex-col gap-2">
          <Btn onClick={updateExisting} loading={saving} testid="dup-update">Update Existing</Btn>
          <Btn variant="outline" onClick={create} testid="dup-new">Save as New</Btn>
          <Btn variant="ghost" onClick={() => setDup(null)} testid="dup-cancel">Cancel</Btn>
        </div>
      </Modal>
      <EventPicker open={picker} onClose={() => setPicker(false)} allowNone selectedIds={new Set(selectedId ? [selectedId] : [])} onPick={(ev) => { setSelectedId(ev?.id ?? null); setPicker(false); }} />
    </Page>
  );
}
